/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, computed, watch, unref, isRef } from 'vue'
import type { Ref } from 'vue'
import type {
  ValidatorConfig,
  Rule,
  FieldRules,
  ValidationErrors,
  SimpleValidatorConfig,
  ValidateOptions
} from './types'

// SECCIÓN 1: FUNCIONES DE UTILIDAD
/**
 * Obtiene un valor de un objeto usando una ruta con notación de puntos
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return obj

  const parts = path.split('.')
  let result = obj

  for (const part of parts) {
    if (result === null || result === undefined) return undefined
    result = result[part]
  }

  return result
}

/**
 * Obtiene un campo de un array usando la notación path[index].field
 */
function getArrayValue(path: string, refs: Record<string, Ref<any>> = {}): any {
  // eslint-disable-next-line no-useless-escape
  const match = path.match(/^([^\[]+)\[(\d+)\]\.(.+)$/)
  if (!match) return undefined

  // Corregir la desestructuración para usar correctamente los grupos capturados
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, arrayName, indexStr, fieldPath] = match
  const index = parseInt(indexStr)

  const arrayRef = refs[arrayName]
  if (!arrayRef) return undefined

  const array = unref(arrayRef)
  if (!Array.isArray(array) || index >= array.length) return undefined

  const item = array[index]
  return getNestedValue(item, fieldPath)
}

/**
 * Obtiene un campo de un objeto usando la notación object.field
 */
function getObjectValue(path: string, refs: Record<string, Ref<any>> = {}): any {
  const [objectName, ...fieldParts] = path.split('.')
  const fieldPath = fieldParts.join('.')

  const objRef = refs[objectName]
  if (!objRef) return undefined

  const obj = unref(objRef)
  return getNestedValue(obj, fieldPath)
}

// SECCIÓN 2: NORMALIZACIÓN DE REGLAS
/**
 * Normaliza las reglas a un formato interno unificado
 */
function normalizeRules(config: ValidatorConfig): Record<string, Rule> {
  const result: Record<string, Rule> = {}

  if (!config) return result

  // Caso 1: Reglas simples { campo: regla }
  if (!('objects' in config) && !('arrays' in config) && !('types' in config)) {
    return config as FieldRules
  }

  // Caso 2: Configuración por tipo
  if ('types' in config && 'currentType' in config) {
    return normalizeTypeRules(config as ValidatorConfig & { types: any; currentType: any })
  }

  // Caso 3: Configuración estructurada (objetos y arrays)
  if ('objects' in config) {
    normalizeObjectRules(config, result)
  }

  return result
}

/**
 * Normaliza reglas basadas en tipos
 */
function normalizeTypeRules(config: ValidatorConfig & { types: any, currentType: any }): Record<string, Rule> {
  const result: Record<string, Rule> = {}

  const currentType = isRef(config.currentType)
    ? config.currentType.value
    : config.currentType

  const typeRules = config.types[currentType]
  if (typeRules) {
    Object.entries(typeRules).forEach(([field, rule]) => {
      result[field] = rule as Rule
    })
  }

  return result
}

/**
 * Normaliza reglas basadas en objetos
 */
function normalizeObjectRules(
  config: ValidatorConfig & { objects?: Record<string, FieldRules> },
  result: Record<string, Rule>
): void {
  const objects = config.objects || {}

  Object.entries(objects).forEach(([objName, objRules]) => {
    Object.entries(objRules).forEach(([fieldName, rule]) => {
      result[`${objName}.${fieldName}`] = rule
    })
  })
}

// SECCIÓN 3: CONFIGURACIÓN DE OBSERVADORES
/**
 * Configura observadores para arrays
 */
function setupArrayWatchers(
  config: ValidatorConfig,
  normalizedRules: Ref<Record<string, Rule>>
): void {
  if (!('arrays' in config) || !config.arrays) return

  const arrays = config.arrays
  const refs = (config as any).refs || {}

  // Aplicar reglas iniciales para los arrays
  Object.entries(arrays).forEach(([arrayName, arrayRules]) => {
    const arrayRef = refs[arrayName]

    if (arrayRef && isRef(arrayRef)) {
      // Aplicar reglas inmediatamente sin esperar al watch
      updateArrayRules(normalizedRules, arrayName, arrayRules, unref(arrayRef) as any[])
      // Configurar observador para actualizaciones futuras
      watch(arrayRef, (newArray) => {
        updateArrayRules(normalizedRules, arrayName, arrayRules, newArray as any[])
      }, { deep: true })
    }
  })
}

/**
 * Actualiza las reglas para un array
 */
function updateArrayRules(
  normalizedRules: Ref<Record<string, Rule>>,
  arrayName: string,
  arrayRules: FieldRules,
  array: any[]
): void {
  if (!Array.isArray(array)) return

  // Debug para verificar la generación de reglas
  // console.log(`Actualizando reglas para ${arrayName}`, array.length)

  // Limpiar reglas anteriores para este array
  Object.keys(normalizedRules.value).forEach(key => {
    if (key.startsWith(`${arrayName}[`)) {
      delete normalizedRules.value[key]
    }
  })

  // Generar reglas para cada elemento
  array.forEach((_, index) => {
    Object.entries(arrayRules).forEach(([fieldName, rule]) => {
      const rulePath = `${arrayName}[${index}].${fieldName}`
      normalizedRules.value[rulePath] = rule
      // Debug para verificar la generación de reglas
      // console.log(`Regla generada: ${rulePath}`)
    })
  })
}

/**
 * Configura observadores para tipos
 */
function setupTypeWatchers(
  config: ValidatorConfig,
  normalizedRules: Ref<Record<string, Rule>>
): void {
  if (!('types' in config) || !('currentType' in config)) return

  const typeRef = config.currentType

  if (isRef(typeRef)) {
    watch(typeRef, (newType) => {
      normalizedRules.value = normalizeRules({
        types: config.types as Record<string, FieldRules>,
        currentType: newType
      })
    })
  }
}

// SECCIÓN 4: FUNCIONES DE VALIDACIÓN
/**
 * Hook principal de validación con API simplificada
 *
 * @example Uso básico simplificado - Sin clasificación explícita de objetos/arrays
 * ```typescript
 * const form = ref({
 *   nombre: '',
 *   email: '',
 *   edad: 0
 * })
 *
 * const validator = useValidator({
 *   // No hace falta especificar "objects" ni "arrays"
 *   form: {
 *     nombre: validators.requerido,
 *     email: validators.correo,
 *     edad: validators.numerico
 *   },
 *   refs: { form }
 * })
 * ```
 *
 * @example Validación de arrays simplificada
 * ```typescript
 * const items = ref([
 *   { nombre: 'Item 1', cantidad: 5 },
 *   { nombre: 'Item 2', cantidad: 0 }
 * ])
 *
 * const validator = useValidator({
 *   // El validador detecta automáticamente que items es un array
 *   items: {
 *     nombre: validators.requerido,
 *     cantidad: validators.numerico
 *   },
 *   refs: { items }
 * })
 * ```
 *
 * @example Validación de campo único (forma simplificada)
 * ```typescript
 * const nombre = ref('')
 * const { validate, error } = useValidator(nombre, validators.requerido)
 * ```
 */
export function useValidator<T>(valueRef: Ref<T>, rule: Rule): { validate: () => boolean, error: Ref<string | undefined>, reset: () => void };
export function useValidator(config: SimpleValidatorConfig | ValidatorConfig): any;
export function useValidator(configOrRef: SimpleValidatorConfig | ValidatorConfig | Ref<any>, rule?: Rule): any {
  // Caso especial: Si es un ref simple + regla
  if (isRef(configOrRef) && rule) {
    return handleSimpleRefValidation(configOrRef as Ref<any>, rule);
  }

  // Convertir config simplificado a formato estándar si es necesario
  const config = convertToStandardConfig(configOrRef as SimpleValidatorConfig | ValidatorConfig);

  const errors = ref<ValidationErrors>({})
  const submitted = ref(false)
  const normalizedRules = ref(normalizeRules(config))

  // Configurar observadores
  setupArrayWatchers(config, normalizedRules)
  setupTypeWatchers(config, normalizedRules)

  // Agregar reglas para referencias simples
  if ('simpleRefs' in config && config.simpleRefs) {
    Object.entries(config.simpleRefs).forEach(([refName, rule]) => {
      normalizedRules.value[refName] = rule;
    });
  }

  /**
   * Obtiene el valor de un campo para validar
   */
  function getFieldValue(path: string): any {
    // Caso: array[índice].campo
    if (path.includes('[')) {
      const value = getArrayValue(path, ('refs' in config) ? (config as any).refs : {})
      // Debug para verificar los valores obtenidos
      // console.log(`Valor para ${path}:`, value)
      return value
    }

    // Caso: objeto.campo
    if (path.includes('.')) {
      return getObjectValue(path, ('refs' in config) ? (config as any).refs : {})
    }

    // Caso: referencia simple (ej: 'miRef')
    if ('refs' in config && config.refs && config.refs[path]) {
      return unref(config.refs[path]);
    }

    // Para validación simple directa (caso no soportado actualmente)
    return null
  }

  /**
   * Valida un campo específico
   */
  function validateField(path: string): boolean {
    const rule = normalizedRules.value[path]
    if (!rule) return true

    const value = getFieldValue(path)
    if (typeof value === 'string' && value.length > 0) {
      // eslint-disable-next-line no-control-regex
      const symbolRegex = /[^\u0000-\u00FF]/
      if (symbolRegex.test(value)) {
        errors.value[path] = 'El campo no puede contener símbolos, emojis o caracteres especiales.'
        return false
      }
    }

    // Obtener el item actual si es un campo de array
    let currentItem = null
    if (path.includes('[') && path.includes(']')) {
      // eslint-disable-next-line no-useless-escape
      const match = path.match(/^([^\[]+)\[(\d+)\]/)
      if (match) {
        const [, arrayName, indexStr] = match
        const index = parseInt(indexStr)
        const arrayRef = ('refs' in config && config.refs) ? config.refs[arrayName] : null
        if (arrayRef) {
          const array = unref(arrayRef)
          if (Array.isArray(array) && index < array.length) {
            currentItem = array[index]
          }
        }
      }
    }

    // Verificar condición dependsOn
    if (typeof rule === 'object' && rule.dependsOn && !rule.dependsOn(currentItem)) {
      delete errors.value[path]
      return true
    }

    return validateValueWithRule(value, rule, path, errors, currentItem)
  }

  /**
   * Valida un valor con una regla
   */
  function validateValueWithRule(
    value: any,
    rule: Rule,
    path: string,
    errors: Ref<ValidationErrors>,
    currentItem?: any,
    context?: any
  ): boolean {
    const validator = typeof rule === 'function' ? rule : rule.validator
    
    // Si es una función de validación simple, llamarla normalmente
    if (typeof rule === 'function') {
      const result = validator(value, currentItem, context)
      
      if (result === true || result === '') {
        delete errors.value[path]
        return true
      } else {
        errors.value[path] = typeof result === 'string' ? result : 'Campo inválido'
        return false
      }
    }
    
    // Si es una regla con configuración, pasarle el contexto si lo necesita
    const result = validator(value, currentItem, context)

    if (result === true || result === '') {
      delete errors.value[path]
      return true
    } else {
      errors.value[path] = typeof result === 'string'
        ? result
        : (rule.message ? rule.message : 'Campo inválido')
      return false
    }
  }

  /**
   * Valida todos los campos configurados
   *
   * @param options Opciones opcionales para la validación
   * @param options.logToConsole Si es true, imprime en consola el detalle de cada campo validado y un resumen
   * @returns true si todos los campos son válidos, false si hay algún error
   *
   * @example Validación normal (sin cambios)
   * ```typescript
   * const isValid = validator.validate()
   * ```
   *
   * @example Validación con logging en consola
   * ```typescript
   * const isValid = validator.validate({ logToConsole: true })
   * // Imprime en consola:
   * // campo.nombre: ✅ VÁLIDO (valor: "Juan")
   * // campo.email: ❌ ERROR: Correo electrónico inválido (valor: "invalid")
   * // 🔍 Resumen de validación
   * // Total campos: 2
   * // Campos válidos: 1
   * // Campos inválidos: 1
   * // ¿Válido general?: ❌ NO
   * ```
   */
  function validate(options?: ValidateOptions): boolean {
    submitted.value = true
    let isValid = true
    const logForced = false // Cambiar a true para forzar logging detallado siempre
    // console.log("Reglas normalizadas:", Object.keys(normalizedRules.value))

    // Validar cada campo
    Object.keys(normalizedRules.value).forEach(path => {
      const fieldValid = validateField(path)
      isValid = isValid && fieldValid

      // Logging opcional detallado
      if (options?.logConsola || logForced ) {
        const error = errors.value[path]
        const value = getFieldValue(path)
        const status = fieldValid ? '✅ VÁLIDO' : `❌ ERROR: ${error}`
        console.log(`${path}: ${status} (valor: ${JSON.stringify(value)})`)
      }
    })

    // Logging opcional del resumen
    if (options?.logConsola || logForced ) {
      const totalFields = Object.keys(normalizedRules.value).length
      const invalidFields = Object.keys(errors.value).length
      const validFields = totalFields - invalidFields

      console.group('🔍 Resumen de validación')
      console.log(`Total campos: ${totalFields}`)
      console.log(`Campos válidos: ${validFields}`)
      console.log(`Campos inválidos: ${invalidFields}`)
      console.log(`¿Válido general?: ${isValid ? '✅ SÍ' : '❌ NO'}`)
      console.groupEnd()
    }

    return isValid
  }

  /**
   * Valida un objeto completo
   */
  function validateItem(item: any, type?: string): boolean {
    submitted.value = true

    let rulesToUse: Record<string, Rule> = {}

    // Si se especifica un tipo, usar las reglas para ese tipo
    if (type && 'types' in config && config.types && config.types[type]) {
      rulesToUse = config.types[type]
    } else {
      // Usar todas las reglas normalizadas
      rulesToUse = normalizedRules.value
    }

    // Validar cada campo del ítem
    Object.entries(rulesToUse).forEach(([field, rule]) => {
      if (field.includes('.') || field.includes('[')) return

      // Verificar condición dependsOn
      if (typeof rule === 'object' && rule.dependsOn && !rule.dependsOn(item)) {
        delete errors.value[field]
        return
      }

      validateValueWithRule(item[field], rule, field, errors, item)
    })

    return !hasAnyError()
  }

  /**
   * Verifica si hay algún error
   */
  function hasAnyError(): boolean {
    return Object.keys(errors.value).length > 0
  }

  /**
   * Obtiene el mensaje de error para un campo
   * Esta función mantiene la compatibilidad con el código anterior
   * @deprecated Usar showError() con template string en su lugar
   */
  function getError(path: string, index?: number, field?: string): string | undefined {
    if (index !== undefined && field !== undefined) {
      // Crear path para campo de array
      path = `${path}[${index}].${field}`
    }

    return submitted.value ? errors.value[path] : undefined
  }

  /**
   * Obtiene el mensaje de error para un campo usando una ruta en formato string template
   *
   * @example
   * // Para objetos
   * showError(`admision.ciadTipoAdmision`)
   *
   * // Para arrays
   * showError(`diagnosticos[${i}].cidiTipoCie10`)
   *
   * // También soporta el formato anterior para compatibilidad
   * showError('admision', 'ciadTipoAdmision')
   * showError('diagnosticos', i, 'cidiTipoCie10')
   */
  function showError(pathOrContext: string, fieldOrIndex?: string | number, field?: string): string | undefined {
    // Caso 1: Formato anterior (mantener compatibilidad)
    if (fieldOrIndex !== undefined) {
      return getError(pathOrContext,
        typeof fieldOrIndex === 'number' ? fieldOrIndex : undefined,
        typeof fieldOrIndex === 'string' ? fieldOrIndex : field);
    }

    // Caso 2: Formato nuevo con template string
    return submitted.value ? errors.value[pathOrContext] : undefined;
  }

  /**
   * Verifica si hay errores en una sección específica o en un elemento específico de un array
   *
   * @example
   * // Verificar errores en todo un contexto
   * hasErrors('admision')
   *
   * // Verificar errores en un campo específico
   * hasErrors('admision.ciadTipoAdmision')
   *
   * // Verificar errores en un elemento específico de un array
   * hasErrors('diagnosticos[2]')
   *
   * // Verificar errores en un campo específico de un elemento de array
   * hasErrors('diagnosticos[2].cidiTipoCie10')
   */
  function hasErrors(path: string, index?: number, field?: string): boolean {
    if (!submitted.value) return false

    // Caso 1: Se proporcionaron index y field - construir la ruta para un elemento específico del array
    if (index !== undefined) {
      const arrayPath = field
        ? `${path}[${index}].${field}`   // Para un campo específico: diagnosticos[2].cidiTipoCie10
        : `${path}[${index}]`            // Para el elemento completo: diagnosticos[2]

      return Object.keys(errors.value).some(key => key.startsWith(arrayPath))
    }

    // Caso 2: Solo se proporcionó la ruta
    return Object.keys(errors.value).some(key => key.startsWith(path))
  }

  /**
   * Limpia los errores de validación sin reiniciar el estado de envío
   * @param context Opcional: contexto específico para limpiar errores (ej: 'admision', 'technologias')
   */
  function clearErrors(context?: string): void {
    if (!context) {
      // Limpiar todos los errores
      errors.value = {}
      return
    }

    // Limpiar solo errores de un contexto específico
    Object.keys(errors.value).forEach(key => {
      if (key.startsWith(context)) {
        delete errors.value[key]
      }
    })
  }

  /**
   * Reinicia el estado de validación
   */
  function reset(): void {
    submitted.value = false
    errors.value = {}
  }

  return {
    // Estado
    errors: computed(() => errors.value),
    submitted,

    // Métodos
    validate,
    validateField,
    validateItem,
    showError,
    hasErrors,
    clearErrors,
    reset
  }
}

/**
 * Convierte una configuración simplificada a la estructura estándar
 * para mantener compatibilidad con el código existente
 */
function convertToStandardConfig(config: SimpleValidatorConfig | ValidatorConfig): ValidatorConfig {
  if ('objects' in config || 'arrays' in config || 'types' in config) {
    return config as ValidatorConfig;
  }

  // Es una configuración simplificada, analizarla y convertirla
  const { refs = {}, ...fieldsConfig } = config as SimpleValidatorConfig;
  const standardConfig: ValidatorConfig = {
    objects: {},
    arrays: {},
    simpleRefs: {}, // Nueva propiedad para referencias simples
    refs
  };

  // Para cada campo, detectar si es un objeto o array y configurarlo adecuadamente
  Object.entries(fieldsConfig).forEach(([key, value]) => {
    if (key === 'refs') return; // Ya lo procesamos arriba

    const refValue = refs[key];
    if (!refValue) {
      console.warn(`No se encontró referencia para "${key}" en los refs proporcionados`);
      return;
    }

    // Detectar el tipo de la referencia
    const unwrappedValue = unref(refValue);

    if (Array.isArray(unwrappedValue)) {
      // Es un array
      standardConfig.arrays![key] = value as FieldRules;
    } else if (typeof unwrappedValue === 'object' && unwrappedValue !== null) {
      // Es un objeto
      standardConfig.objects![key] = value as FieldRules;
    } else {
      // Es un valor simple (string, number, boolean, etc.)
      standardConfig.simpleRefs![key] = value as unknown as Rule;
    }
  });

  return standardConfig;
}

/**
 * Maneja la validación para una referencia simple
 */
function handleSimpleRefValidation<T>(valueRef: Ref<T>, rule: Rule) {
  const error = ref<string | undefined>(undefined)
  const submitted = ref(false)

  const validate = (): boolean => {
    submitted.value = true
    const validator = typeof rule === 'function' ? rule : rule.validator
    const result = validator(valueRef.value)

    if (result === true || result === '') {
      error.value = undefined
      return true
    } else {
      error.value = typeof result === 'string'
        ? result
        : (typeof rule === 'object' && rule.message ? rule.message : 'Campo inválido')
      return false
    }
  }

  return {
    validate,
    error: computed(() => submitted.value ? error.value : undefined),
    reset: () => {
      submitted.value = false
      error.value = undefined
    }
  }
}

// Exportar también como default para facilitar importaciones
export default useValidator
